import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/our-model", label: "Our Model" },
  { to: "/team", label: "Team & Advisors" },
  { to: "/umcc", label: "Policy" },
  { to: "/investors", label: "Investors & Partners" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-bone/85 border-b border-line/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[13px] tracking-wide transition-colors ${
                  isActive ? "text-forest" : "text-ink/75 hover:text-ink"
                } after:absolute after:left-0 after:-bottom-1 after:h-px after:bg-forest after:transition-all ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center">
          <Link
            to="/contact"
            data-testid="header-cta-inquiries"
            className="group inline-flex items-center gap-2 text-[13px] tracking-wide bg-forest text-bone px-5 py-2.5 hover:bg-forest/85 transition-colors"
          >
            Inquiries
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-ink"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-bone border-t border-line/60" data-testid="mobile-menu-panel">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-forest" : "text-ink"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-inquiries"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-forest text-bone py-3"
            >
              Investor & Partnership Inquiries
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
